import { useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Slider from '@mui/material/Slider';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';

import { paths } from 'src/routes/paths';

import { endpoints, postFetcher } from 'src/lib/axios';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';

import { useCreditPacks } from '../hooks/useCreditPacks';
import { useSubscription } from '../hooks/useSubscription';
import { extractPackPurchaseError } from '../types/billing';
import { useCreditPurchase } from '../context/CreditPurchaseContext';

import type { CreditFeaturePricing } from '../types/billing';

// ----------------------------------------------------------------------
// Per-unit credit purchase modal — replaces PackPickerModal. Two
// independent sliders (Screening + Core Interview), live order summary
// with empty/configured states, and a one-shot Stripe Checkout redirect.
// No bundle discount. Mounted globally inside CreditPurchaseProvider.
// ----------------------------------------------------------------------

const SLIDER_MIN = 0;

function formatUsd(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatTrialEnd(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function snapToIncrement(raw: number, increment: number, max: number): number {
  if (Number.isNaN(raw) || raw <= 0) return 0;
  const clamped = Math.min(max, raw);
  return Math.round(clamped / increment) * increment;
}

// ----------------------------------------------------------------------

export function CreditPurchaseModal() {
  const theme = useTheme();
  const {
    isOpen,
    prefillScreeningCredits,
    prefillInterviewCredits,
    onPurchaseComplete,
    close,
  } = useCreditPurchase();
  const { pricing } = useCreditPacks();
  const { currentPlan, subscription } = useSubscription();

  const isOnPaidPlan = Boolean(currentPlan) && currentPlan !== 'free';
  const isInTrial = Boolean(subscription?.isInTrial);
  const trialEndLabel = formatTrialEnd(subscription?.trialEnd ?? null);
  const isEligible = isOnPaidPlan && !isInTrial;

  const sliderMax = pricing?.maxPurchaseUnits ?? 500;
  const sliderStep = pricing?.purchaseIncrementUnits ?? 10;

  const [screeningQty, setScreeningQty] = useState(0);
  const [interviewQty, setInterviewQty] = useState(0);
  const [pending, setPending] = useState(false);

  // Reset on every open so prefills land clean and stale pending/qty doesn't leak across opens
  useEffect(() => {
    if (!isOpen) return;
    setScreeningQty(prefillScreeningCredits ?? 0);
    setInterviewQty(prefillInterviewCredits ?? 0);
    setPending(false);
  }, [isOpen, prefillScreeningCredits, prefillInterviewCredits]);

  const screeningSubtotalCents = pricing?.screening
    ? screeningQty * pricing.screening.pricePerUnitUsdCents
    : 0;
  const interviewSubtotalCents = pricing?.interviews
    ? interviewQty * pricing.interviews.pricePerUnitUsdCents
    : 0;
  const totalCents = screeningSubtotalCents + interviewSubtotalCents;
  const canCheckout = totalCents > 0 && isEligible && !pending;

  const handleClose = useCallback(() => {
    if (pending) return;
    close();
  }, [pending, close]);

  const handleCheckout = useCallback(async () => {
    if (!canCheckout) return;
    setPending(true);
    try {
      const res = (await postFetcher([
        endpoints.billing.purchaseCredits,
        {
          screeningCredits: screeningQty,
          interviewCredits: interviewQty,
        },
      ])) as Record<string, unknown>;
      const url =
        (res.checkoutUrl as string | undefined) ||
        (res.CheckoutUrl as string | undefined) ||
        (res.url as string | undefined) ||
        (res.Url as string | undefined);
      if (!url) throw new Error('No checkout URL returned by server');
      onPurchaseComplete?.();
      window.location.href = url;
    } catch (err) {
      const { code, message } = extractPackPurchaseError(err);
      switch (code) {
        case 'PACK_PURCHASE_REQUIRES_PAID_PLAN':
          toast.error('Paid plan required', { description: message });
          close();
          break;
        case 'PACK_PURCHASE_BLOCKED_DURING_TRIAL': {
          const description = trialEndLabel
            ? `Pack purchases unlock after your free trial ends on ${trialEndLabel}.`
            : message;
          toast.info('Available after trial', { description });
          close();
          break;
        }
        case 'PACK_PURCHASE_IN_PROGRESS':
          toast.error('Purchase already in progress', { description: message });
          break;
        case 'INVALID_CREDIT_QUANTITY':
          toast.error('Invalid quantity', {
            description: 'Quantities must be in multiples of 10.',
          });
          break;
        case null:
        default:
          toast.error('Checkout failed', { description: message });
          break;
      }
      setPending(false);
    }
  }, [
    canCheckout,
    screeningQty,
    interviewQty,
    onPurchaseComplete,
    close,
    trialEndLabel,
  ]);

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="credit-purchase-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2.5,
            overflow: 'hidden',
            maxWidth: 560,
          },
        },
      }}
    >
      <HeaderBand onClose={handleClose} pending={pending} />

      <TrustStrip />

      <DialogContent
        aria-busy={pending}
        sx={{ p: { xs: 3, sm: 4 }, bgcolor: 'background.paper' }}
      >
        {!isOnPaidPlan && (
          <Alert severity="info" role="status" sx={{ mb: 2 }}>
            Pack purchases require a paid plan.{' '}
            <Link
              href={paths.orgSettings.billing}
              onClick={handleClose}
              sx={{ fontWeight: 600 }}
            >
              Upgrade your plan
            </Link>
          </Alert>
        )}
        {isOnPaidPlan && isInTrial && (
          <Alert severity="info" role="status" sx={{ mb: 2 }}>
            {trialEndLabel
              ? `Pack purchases unlock after your free trial ends on ${trialEndLabel}.`
              : 'Pack purchases unlock after your free trial ends.'}
          </Alert>
        )}

        <CreditSelector
          icon="lucide:mic"
          title="AI Screening Credits"
          subtitle="Automated phone screens with candidates"
          pricing={pricing?.screening ?? null}
          value={screeningQty}
          onChange={setScreeningQty}
          min={SLIDER_MIN}
          max={sliderMax}
          step={sliderStep}
          disabled={!isEligible || pending}
          ariaLabel="Number of screening credits"
        />

        <Divider sx={{ my: 3 }} />

        <CreditSelector
          icon="lucide:code-2"
          title="AI Core Interview Credits"
          subtitle="Live technical and behavioral interviews"
          pricing={pricing?.interviews ?? null}
          value={interviewQty}
          onChange={setInterviewQty}
          min={SLIDER_MIN}
          max={sliderMax}
          step={sliderStep}
          disabled={!isEligible || pending}
          ariaLabel="Number of core interview credits"
        />

        <OrderSummary
          screeningQty={screeningQty}
          interviewQty={interviewQty}
          screeningSubtotalCents={screeningSubtotalCents}
          interviewSubtotalCents={interviewSubtotalCents}
          totalCents={totalCents}
        />

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1.5}
          sx={{ mt: 3 }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={pending}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleCheckout}
            disabled={!canCheckout}
            startIcon={
              pending ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : undefined
            }
            endIcon={!pending && totalCents > 0 ? <Iconify icon="mdi:arrow-right" width={14} /> : undefined}
            aria-label={
              totalCents > 0
                ? `Continue to checkout, ${formatUsd(totalCents)} total`
                : 'Select credits to continue'
            }
            sx={{ flex: 2 }}
          >
            {pending
              ? 'Processing…'
              : totalCents === 0
                ? 'Select credits'
                : `Continue to checkout · ${formatUsd(totalCents)}`}
          </Button>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            mt: 2.5,
            p: 2,
            bgcolor: alpha(theme.palette.grey[500], 0.08),
            borderRadius: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Iconify
            icon="solar:info-circle-linear"
            width={16}
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1 }}>
            One-time purchase · Charged immediately to your card on file · Plan allowance is consumed before credits.
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------
// Header band — purple gradient, sparkle eyebrow, headline, close button
// ----------------------------------------------------------------------

type HeaderBandProps = {
  onClose: () => void;
  pending: boolean;
};

function HeaderBand({ onClose, pending }: HeaderBandProps) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        px: { xs: 3, sm: 4 },
        py: 3.5,
        color: 'common.white',
        background: `linear-gradient(135deg, ${theme.palette.primary.darker} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.primary.main} 100%)`,
        position: 'relative',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{
              display: 'inline-flex',
              bgcolor: alpha(theme.palette.common.white, 0.18),
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              mb: 1.5,
            }}
          >
            <Iconify
              icon="solar:rocket-bold"
              width={12}
              sx={{ color: 'common.white' }}
            />
            <Typography
              variant="overline"
              sx={{
                color: 'common.white',
                lineHeight: 1,
                letterSpacing: '0.08em',
              }}
            >
              Custom packs
            </Typography>
          </Stack>
          <Typography
            id="credit-purchase-title"
            variant="h4"
            sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}
          >
            Pay only for what you need
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.5,
              color: alpha(theme.palette.common.white, 0.86),
              maxWidth: 460,
            }}
          >
            Top up screenings or core interviews — one-time charge, no subscription change.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={pending}
          aria-label="Close"
          sx={{
            bgcolor: alpha(theme.palette.common.white, 0.16),
            color: 'common.white',
            '&:hover': {
              bgcolor: alpha(theme.palette.common.white, 0.24),
            },
          }}
        >
          <Iconify icon="mingcute:close-line" width={18} />
        </IconButton>
      </Stack>
    </Box>
  );
}

// ----------------------------------------------------------------------
// Trust strip — three icon+label items below the gradient header
// ----------------------------------------------------------------------

const TRUST_ITEMS: ReadonlyArray<{ icon: string; label: string }> = [
  { icon: 'solar:bolt-bold', label: '12-month validity' },
  { icon: 'solar:shield-check-bold', label: 'No subscription' },
  { icon: 'solar:bolt-bold', label: 'Instant top-up' },
];

function TrustStrip() {
  const theme = useTheme();
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 1, sm: 2 }}
      justifyContent="space-around"
      sx={{
        px: { xs: 3, sm: 4 },
        py: 1.5,
        bgcolor: alpha(theme.palette.grey[500], 0.04),
        borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
      }}
    >
      {TRUST_ITEMS.map((item) => (
        <Stack
          key={item.label}
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent={{ xs: 'flex-start', sm: 'center' }}
        >
          <Iconify
            icon={item.icon}
            width={14}
            sx={{ color: 'primary.main' }}
          />
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600 }}
          >
            {item.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// CreditSelector — header row, slider, stepper + number input + subtotal
// ----------------------------------------------------------------------

type CreditSelectorProps = {
  icon: string;
  title: string;
  subtitle: string;
  pricing: CreditFeaturePricing | null;
  value: number;
  onChange: (next: number) => void;
  min: number;
  max: number;
  step: number;
  disabled: boolean;
  ariaLabel: string;
};

function CreditSelector({
  icon,
  title,
  subtitle,
  pricing,
  value,
  onChange,
  min,
  max,
  step,
  disabled,
  ariaLabel,
}: CreditSelectorProps) {
  const theme = useTheme();
  const [draft, setDraft] = useState(String(value));

  // Sync draft text whenever the canonical value changes (slider, stepper, prefill, reset)
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const subtotalCents = pricing ? value * pricing.pricePerUnitUsdCents : 0;
  const pricePerUnit = pricing ? formatUsd(pricing.pricePerUnitUsdCents) : '—';

  const marks = useMemo(
    () => buildMarks(max, step),
    [max, step]
  );

  const handleSliderChange = (_: Event, raw: number | number[]) => {
    const next = typeof raw === 'number' ? raw : raw[0];
    onChange(next);
  };

  const handleDecrement = () => {
    onChange(Math.max(min, value - step));
  };
  const handleIncrement = () => {
    onChange(Math.min(max, value + step));
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value.replace(/[^0-9]/g, ''));
  };
  const handleInputBlur = () => {
    const parsed = Number.parseInt(draft || '0', 10);
    const snapped = snapToIncrement(parsed, step, max);
    onChange(snapped);
    setDraft(String(snapped));
  };

  return (
    <Box sx={{ opacity: disabled ? 0.5 : 1 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Box
          aria-hidden
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.25,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Iconify icon={icon} width={18} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            spacing={1}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {pricePerUnit}
              </Typography>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', lineHeight: 1 }}
              >
                per credit
              </Typography>
            </Stack>
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ px: 1 }}>
        <Slider
          value={value}
          onChange={handleSliderChange}
          min={min}
          max={max}
          step={step}
          marks={marks}
          disabled={disabled || !pricing}
          aria-label={ariaLabel}
          sx={{
            '& .MuiSlider-markLabel': {
              fontSize: theme.typography.caption.fontSize,
              color: 'text.secondary',
              fontWeight: 600,
            },
            '& .MuiSlider-mark': {
              height: 6,
              width: 2,
              borderRadius: 1,
            },
          }}
        />
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.5}
        sx={{ mt: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            onClick={handleDecrement}
            disabled={disabled || !pricing || value <= min}
            aria-label={`Decrease ${ariaLabel.toLowerCase()} by ${step}`}
            size="small"
            sx={{
              border: `1px solid ${alpha(theme.palette.grey[500], 0.32)}`,
              borderRadius: 1,
            }}
          >
            <Iconify icon="mdi:minus" width={14} />
          </IconButton>
          <TextField
            value={draft}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            disabled={disabled || !pricing}
            size="small"
            inputProps={{
              inputMode: 'numeric',
              'aria-label': `${ariaLabel} number input`,
            }}
            sx={{
              width: 88,
              '& input': { textAlign: 'center', fontWeight: 700 },
            }}
          />
          <IconButton
            onClick={handleIncrement}
            disabled={disabled || !pricing || value >= max}
            aria-label={`Increase ${ariaLabel.toLowerCase()} by ${step}`}
            size="small"
            sx={{
              border: `1px solid ${alpha(theme.palette.grey[500], 0.32)}`,
              borderRadius: 1,
            }}
          >
            <Iconify icon="mdi:plus" width={14} />
          </IconButton>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            credits
          </Typography>
        </Stack>

        <Stack direction="row" spacing={0.75} alignItems="baseline">
          <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1 }}>
            Subtotal
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {formatUsd(subtotalCents)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

// Slider marks at 0, ~20%, ~50%, max — snapped to the increment so labels line up with values
function buildMarks(max: number, step: number): Array<{ value: number; label: string }> {
  const safeStep = step > 0 ? step : 1;
  const snap = (n: number) => Math.round(n / safeStep) * safeStep;
  const mid1 = snap(max * 0.2);
  const mid2 = snap(max * 0.5);
  const seen = new Set<number>();
  return [0, mid1, mid2, max]
    .filter((n) => {
      if (seen.has(n)) return false;
      seen.add(n);
      return true;
    })
    .sort((a, b) => a - b)
    .map((value) => ({ value, label: String(value) }));
}

// ----------------------------------------------------------------------
// Order summary — empty (dashed) vs configured (solid tint) states
// ----------------------------------------------------------------------

type OrderSummaryProps = {
  screeningQty: number;
  interviewQty: number;
  screeningSubtotalCents: number;
  interviewSubtotalCents: number;
  totalCents: number;
};

function OrderSummary({
  screeningQty,
  interviewQty,
  screeningSubtotalCents,
  interviewSubtotalCents,
  totalCents,
}: OrderSummaryProps) {
  const theme = useTheme();
  const isEmpty = screeningQty === 0 && interviewQty === 0;

  if (isEmpty) {
    return (
      <Stack
        spacing={0.75}
        alignItems="center"
        justifyContent="center"
        sx={{
          mt: 3,
          p: 3,
          borderRadius: 1.5,
          border: `1px dashed ${alpha(theme.palette.primary.main, 0.32)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          textAlign: 'center',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 36,
            height: 36,
            borderRadius: 999,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Iconify icon="mdi:plus" width={18} />
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Your order
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Move a slider to start your order
        </Typography>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        mt: 3,
        p: 2.5,
        borderRadius: 1.5,
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
      }}
    >
      <Typography
        variant="overline"
        sx={{
          display: 'block',
          fontWeight: 800,
          color: 'primary.main',
          letterSpacing: '0.08em',
          mb: 1.5,
        }}
      >
        Your order
      </Typography>

      <Stack spacing={1}>
        {screeningQty > 0 && (
          <SummaryRow
            label={`${screeningQty.toLocaleString()} screening credits`}
            amount={formatUsd(screeningSubtotalCents)}
          />
        )}
        {interviewQty > 0 && (
          <SummaryRow
            label={`${interviewQty.toLocaleString()} core interview credits`}
            amount={formatUsd(interviewSubtotalCents)}
          />
        )}
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Total
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          {formatUsd(totalCents)}
        </Typography>
      </Stack>
    </Box>
  );
}

function SummaryRow({ label, amount }: { label: string; amount: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          aria-hidden
          sx={{
            width: 4,
            height: 4,
            borderRadius: 999,
            bgcolor: 'primary.main',
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
        {amount}
      </Typography>
    </Stack>
  );
}

